// Device-local call history. Call metadata is deliberately NOT sourced from
// the server (privacy: the server should not be the keeper of who called whom
// for this app) — the shell appends an entry whenever the engine publishes an
// ended call, and the screens render this log only. Works fully offline.
//
// Same pattern as `favorites.ts`/`serverCache.ts`: synchronous localStorage
// with a versioned key, a referentially stable in-memory snapshot
// (`useSyncExternalStore`-friendly), and a `reset…` test hook. Entries reuse
// the protocol `CallLogEntry` TYPE so the screens' rendering (describeCall)
// needs no second vocabulary — the type is imported, never redefined.

import { z } from 'zod';
import { CallLogEntry } from '@merkury/protocol';
import type { CallOutcome, Friend } from '@merkury/protocol';
import type { EndedCallState } from '@merkury/brain/call/types.js';
import { getCachedFriends } from './serverCache.js';

/**
 * A stored entry additionally snapshots the peer's display name at call time.
 * Device-local by design, so it EXTENDS the protocol type rather than changing
 * it: the server's call log stays metadata-only (privacy — the server never
 * learns display-name↔call associations), and history keeps a human name even
 * after the friendship is severed (blocking severs it server-side, docs/02 §3,
 * which used to turn every blocked peer's history into "Unknown caller").
 */
export const StoredCallLogEntry = CallLogEntry.extend({
  peerName: z.string().min(1).optional(),
});
export type StoredCallLogEntry = z.infer<typeof StoredCallLogEntry>;

const KEY = 'merkury.calllog.v1';
/** Keep only the most recent entries — a call log, not an archive. */
export const CALL_LOG_CAP = 200;

type Listener = () => void;
const listeners = new Set<Listener>();

// Newest-first, stable reference between writes.
let cache: readonly StoredCallLogEntry[] | undefined;

function ls(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Storage can throw (disabled cookies, sandboxed iframe) — treat as absent.
    return undefined;
  }
}

const Snapshot = z.array(StoredCallLogEntry);

function load(): StoredCallLogEntry[] {
  const raw = ls()?.getItem(KEY);
  if (!raw) return [];
  try {
    const parsed = Snapshot.safeParse(JSON.parse(raw));
    return parsed.success ? backfillNames(parsed.data) : [];
  } catch {
    return [];
  }
}

/**
 * Entries logged before name snapshots existed (or while the friends cache was
 * empty) get their name filled in from the current friends cache on hydrate.
 * This also heals history after an unblock + re-friend: the moment the peer is
 * a cached friend again, their old rows regain the name on the next boot.
 */
function backfillNames(entries: StoredCallLogEntry[]): StoredCallLogEntry[] {
  const friends = getCachedFriends()?.data;
  if (!friends?.length) return entries;
  let changed = false;
  const filled = entries.map((e) => {
    if (e.peerName) return e;
    const name = nameOf(e.peerUserId, friends);
    if (!name) return e;
    changed = true;
    return { ...e, peerName: name };
  });
  if (changed) {
    try {
      ls()?.setItem(KEY, JSON.stringify(filled));
    } catch {
      // Quota failure — the hydrated copy still serves this session.
    }
  }
  return filled;
}

function nameOf(userId: string, friends: readonly Friend[]): string | undefined {
  const name = friends.find((f) => f.userId === userId)?.displayName;
  return name || undefined;
}

function persist(entries: StoredCallLogEntry[]): void {
  cache = entries;
  try {
    ls()?.setItem(KEY, JSON.stringify(entries));
  } catch {
    // Quota failure — the in-memory log still serves this session.
  }
  for (const listener of listeners) listener();
}

/** Subscribe to log changes (for `useSyncExternalStore`). */
export function subscribeCallLog(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The call log, newest first. Referentially stable until it changes. */
export function getCallLog(): readonly StoredCallLogEntry[] {
  if (cache === undefined) cache = load();
  return cache;
}

/**
 * Map the engine's end cause onto the log's outcome vocabulary (the one
 * `describeCall` renders). `connected` = ICE ever connected: a hangup or a
 * lost connection after connecting is a completed call (it happened); before
 * connecting it never did. `answered_elsewhere` returns null — the device
 * that answered logs it; this one neither missed nor took it.
 */
function outcomeFor(ended: EndedCallState, connected: boolean): CallOutcome | null {
  switch (ended.cause) {
    case 'hangup':
      if (connected) return 'completed';
      // Never connected: an outgoing cancel reads "no answer" (missed,
      // outgoing); an incoming hangup while ringing is a decline.
      return ended.direction === 'outgoing' ? 'missed' : 'declined';
    case 'connection_lost':
      return connected ? 'completed' : 'failed';
    case 'declined':
    case 'busy':
      return 'declined';
    case 'no_answer': // outgoing ring timed out — renders "no answer"
    case 'missed':
      return 'missed';
    case 'answered_elsewhere':
      return null;
    case 'failed':
    case 'mic_denied':
    case 'security':
      return 'failed';
  }
}

/**
 * Append the engine's ended call to the log. The connect time comes from the
 * ended state itself (`ended.connectedAt`, ms epoch) — it drives both the
 * completed/never-connected classification and the completed-call duration.
 * Idempotent per callId — a double-fired subscriber must not duplicate
 * history. Returns the entry, or undefined when nothing is logged (answered
 * elsewhere / duplicate).
 */
export function recordEndedCall(ended: EndedCallState): StoredCallLogEntry | undefined {
  const current = getCallLog();
  if (current.some((e) => e.id === ended.callId)) return undefined;
  const connectedAt = ended.connectedAt;
  const connected = connectedAt !== null;
  const outcome = outcomeFor(ended, connected);
  if (outcome === null) return undefined;
  const peerName = nameOf(ended.peerUserId, getCachedFriends()?.data ?? []);
  const entry: StoredCallLogEntry = {
    id: ended.callId,
    peerUserId: ended.peerUserId,
    ...(peerName ? { peerName } : {}),
    direction: ended.direction,
    // The log's startedAt anchors both the row timestamp and the duration
    // (endedAt - startedAt): use connect time when there is one, end time
    // otherwise (ring start isn't in EndedCallState — see module note).
    startedAt: new Date(connected ? connectedAt : ended.endedAt).toISOString(),
    ...(outcome === 'completed' ? { endedAt: new Date(ended.endedAt).toISOString() } : {}),
    outcome,
    // Only calls you didn't know about badge: incoming missed. Your own
    // unanswered outgoing ("no answer") was watched happening.
    seen: !(outcome === 'missed' && ended.direction === 'incoming'),
  };
  persist([entry, ...current].slice(0, CALL_LOG_CAP));
  return entry;
}

/**
 * How many incoming missed calls are still unseen — the same set the in-app
 * Recents badge counts (MainScreen), exposed so the shell can mirror it to the
 * native launcher badge. Referentially stable read; recomputed from the log.
 */
export function getUnseenMissedCount(): number {
  return getCallLog().filter((e) => e.outcome === 'missed' && !e.seen).length;
}

/** Clear the missed-call badge: mark every missed entry seen. Local, sync. */
export function markAllMissedSeen(): void {
  const current = getCallLog();
  if (!current.some((e) => e.outcome === 'missed' && !e.seen)) return;
  persist(current.map((e) => (e.outcome === 'missed' && !e.seen ? { ...e, seen: true } : e)));
}

/** Wipe the log — sign-out must not leave the previous account's history. */
export function clearCallLog(): void {
  cache = [];
  ls()?.removeItem(KEY);
  for (const listener of listeners) listener();
}

/** Test seam: drop the in-memory snapshot so the next read re-hydrates. */
export function resetCallLogCache(): void {
  cache = undefined;
}
